import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { getEmbeddedResource, getUrl, hasEmbeddedResource } from '@ngxp/rest';
import { AdressverwaltungFacade } from '@schir-int-client/adressverwaltung-shared';
import { isNull } from 'lodash-es';
import { forkJoin } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
	clearAdressatenByAufgabeAction,
	enrichAdressatenAction,
	loadAdressatenByAufgabeAction,
	loadAdressatenSuccessAction,
	loadAufgabeSuccessAction,
	updateAufgabeAction,
} from '../aufgabe.actions';
import { AufgabeFacade } from '../aufgabe.facade';
import { AdressatResource, AdressatWithResource, AufgabeResource } from '../aufgabe.model';
import {
	createAdressatAction,
	deleteAdressatAction,
	updateAdressatChannelAction,
	updateAdressatNotizAction,
} from './adressat.actions';
import { AdressatLinkRel, AdressatListLinkRel } from './adressat.linkrel';
import { AdressatService } from './adressat.service';

@Injectable()
export class AdressatEffect {
	private aufgabeDefiningAdressaten: AufgabeResource;

	constructor(
		private actions: Actions,
		private adressverwaltungFacade: AdressverwaltungFacade,
		private adressatService: AdressatService,
		private aufgabeFacade: AufgabeFacade
	) {}

	loadAdressatenInAufgabe = createEffect(() =>
		this.actions.pipe(
			ofType(loadAdressatenByAufgabeAction, loadAufgabeSuccessAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			filter(([, aufgabe]) => {
				// FINDME: diese Bremse ist an der falschen Stelle aber wurde hier minimalinvasiv eingebaut, da Ladevorgang nicht vom PO entschieden
				if (aufgabe === this.aufgabeDefiningAdressaten) {
					return false;
				}
				this.aufgabeDefiningAdressaten = aufgabe;
				return true;
			}),
			switchMap(([, aufgabe]) =>
				this.adressatService
					.getAdressaten(aufgabe)
					.pipe(map(adressaten => loadAdressatenSuccessAction({ aufgabeUri: getUrl(aufgabe), adressaten })))
			)
		)
	);

	enrichAdressatenWithResource = createEffect(() =>
		this.actions.pipe(
			ofType(loadAdressatenSuccessAction),
			switchMap(({ aufgabeUri, adressaten }) => {
				const adressatenRes: AdressatResource[] = getEmbeddedResource(
					adressaten,
					AdressatListLinkRel.ADRESSAT_LIST
				);
				const adressatenWithResource: AdressatWithResource[] = isNull(adressatenRes)
					? []
					: adressatenRes.map(adressat => {
							return { ...adressat, resource: null };
						});
				return forkJoin(
					adressatenWithResource.map(adressat => {
						return this.adressverwaltungFacade
							.getKontaktByUri(getUrl(adressat, AdressatLinkRel.KONTAKT))
							.pipe(
								take(1),
								map(kontakt => <AdressatWithResource>{ ...adressat, resource: kontakt })
							);
					})
				).pipe(map(adressaten => enrichAdressatenAction({ aufgabeUri, adressaten })));
			})
		)
	);

	createAdressat = createEffect(() =>
		this.actions.pipe(
			ofType(createAdressatAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			switchMap(([{ adressat, kontaktId }, aufgabe]) =>
				this.adressatService.createAdressat(aufgabe, kontaktId, adressat).pipe(
					map(() => {
						this.aufgabeDefiningAdressaten = null;
						return loadAdressatenByAufgabeAction({ aufgabe });
					})
				)
			)
		)
	);

	updateChannel = createEffect(() =>
		this.actions.pipe(
			ofType(updateAdressatChannelAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			switchMap(([{ adressat }, aufgabe]) =>
				this.adressatService
					.updateChannel(adressat)
					.pipe(map(adressaten => loadAdressatenSuccessAction({ aufgabeUri: getUrl(aufgabe), adressaten })))
			)
		)
	);

	updateNotiz = createEffect(() =>
		this.actions.pipe(
			ofType(updateAdressatNotizAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			switchMap(([{ adressat }, aufgabe]) =>
				this.adressatService
					.updateNotiz(adressat)
					.pipe(map(adressaten => loadAdressatenSuccessAction({ aufgabeUri: getUrl(aufgabe), adressaten })))
			)
		)
	);

	deleteAdressat = createEffect(() =>
		this.actions.pipe(
			ofType(deleteAdressatAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			switchMap(([{ adressat }, aufgabe]) => {
				return this.adressatService.deleteAdressat(adressat).pipe(
					mergeMap(adressaten => {
						if (hasEmbeddedResource(adressaten, AdressatListLinkRel.ADRESSAT_LIST)) {
							return [loadAdressatenSuccessAction({ aufgabeUri: getUrl(aufgabe), adressaten })];
						} else {
							return [
								clearAdressatenByAufgabeAction({ aufgabeUri: getUrl(aufgabe) }),
								updateAufgabeAction({ aufgabe }),
							];
						}
					})
				);
			})
		)
	);
}
